import {
  createApi, fetchBaseQuery, BaseQueryFn,
  FetchArgs, FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { API_URL } from "../config";
import {
  IApiCustomerResponse,
  IApiLoopingResponse,
  IApiResponse,
  IShopOrder,
  IApiResponseInvoice,
  IApiResponseWorkOrder,
  ICategoryFilterPayload,
  ICategoryPayload,
  ICustomerProjectPayload,
  ICustomerTransactionPayload,
  IGetByCategoryArrayPayload,
  IGetByCategoryPayload,
  IInvoiceListPayload,
  ILoopingTableListPayload,
  IPriceHistoryPayload,
  IProductPayload,
  IVariantPayload,
  IModifierPayload,
  IModifierGroupPayload,
  IRemovedorkOrderByCustomerProjectPayload,
  ISearchIsActivePayload,
  ISearchPayload,
  ISelectCustomerPayload,
  IUpdateStatuInvoicePayload,
  IUpdateWorkOrderByCustomerProjectPayload,
  IUser,
  IWorkOrderByCustomerProjectPayload,
  IWorkOrderHistoryPayload,
  IEmail,
  IText,
  INote,
  INoteEmail,
  IActivityHistoryApiResponse,
  IActivityHistoryProjectApiResponse,
  IInvoiceHistoryPayload,
  IAssignDriverPayload,
  IApiShopProductResponse,
  ISearchRoutePayload,
} from "./Interface/api.interface";
/**
 * Holds all the API callbacks
 * @returns RTK Implementation for backend
 */

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { endpoint }) => {
    // Most mutations in this app send a PRE-STRINGIFIED JSON string body
    // (body: JSON.stringify(...)). fetchBaseQuery only auto-sets the JSON
    // content-type for *object* bodies, so those string-body requests rely
    // on this header being set explicitly — without it Laravel can't parse
    // the payload and every field validates as "required" (e.g. login's
    // username/password). So we DO set it here, but we must NOT set it for
    // the multipart FormData upload, where the browser has to set
    // "multipart/form-data; boundary=…" itself (otherwise PHP parses no
    // fields — the APK upload's "platform field is required").
    // NOTE: RTK's prepareHeaders api doesn't expose the request body/arg,
    // so we key off the endpoint name. Any new FormData upload endpoint
    // must be excluded here too.
    if (
      endpoint !== "uploadAppRelease" &&
      endpoint !== "inspectAppRelease" &&
      endpoint !== "uploadCompanyLogo"
    ) {
      headers.set("Content-Type", "application/json");
    }
    // Always ask for JSON so the API returns 422 validation errors as JSON
    // instead of Laravel's default 302 redirect-back.
    headers.set("Accept", "application/json");
    headers.set(
      "Authorization",
      `Bearer ${localStorage.getItem("token") || ""}`
    );
    return headers;
  },
});

/**
 * Wrap fetchBaseQuery so any 401 from a Sanctum endpoint clears the
 * stale token + the cached user, then hard-navigates to /login.
 * Without this, an expired/revoked token surfaces as "Unauthenticated"
 * on every action the user takes (create company, add device, etc.)
 * with no way back into a working session except a manual logout.
 *
 * We deliberately don't try to refresh — there's no refresh-token
 * endpoint in this API. The only valid recovery is re-login.
 *
 * Excludes /login itself from the redirect so a bad password doesn't
 * trigger a navigation loop.
 */
/**
 * Public player / portal routes that must NEVER be bounced to the admin
 * login on a 401. The browser player authenticates with its own device
 * token (a separate fetch client); an incidental 401 from an admin RTK
 * query on these routes must not hard-navigate the signage screen to
 * /login. Keep this in sync with LoginProvider / Menu portal-route logic.
 */
const isPublicPortalPath = (p: string): boolean =>
  p === "/player" || p.startsWith("/player/") ||
  p === "/p" || p.startsWith("/p/") ||
  p.startsWith("/installer") || p.startsWith("/download/") ||
  p.includes("/restaurant-portal/") || p === "/restaurant-portal/login" ||
  /\/devices\/\d+\/print-sheet/.test(p);

const baseQueryWith401Handler: BaseQueryFn<
  string | FetchArgs, unknown, FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    const url = typeof args === "string" ? args : args.url;
    const isLoginCall = typeof url === "string" && url.includes("login");
    const onPortal = typeof window !== "undefined"
      && isPublicPortalPath(window.location.pathname);
    if (!isLoginCall && !onPortal) {
      try {
        localStorage.removeItem("token");
        localStorage.removeItem("current_user");
      } catch { /* SSR / quota errors are non-fatal */ }
      // Hard navigation so the auth slice re-initialises with
      // isAuthorized=false and the router lands on the login page.
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        const here = window.location.pathname + window.location.search;
        window.location.replace(`/login?next=${encodeURIComponent(here)}&reason=session_expired`);
      }
    }
  }
  return result;
};

export const attoDeskApi = createApi({
  reducerPath: "attoDeskApi",
  baseQuery: baseQueryWith401Handler,
  tagTypes: [
    "User",
    "shopOrder",
    "shopInvoice",
    "category",
    "modifierCategory",
    "company",
    "department",
    "coursing",
    "tax",
    "product",
    "event",
    "printer",
    "productBrand",
    "productTag",
    "productCategory",
    "modifier",
    "modifierGroup",
    "modifierGrp",
    "productGetByCategory",
    "looping",
    "customer",
    "shop",
    "route",
    "shopProduct",
    "vendor",
    "inventory",
    "sold",
    "bill",
    "invoiceHistroy",
    "invoiceHistroyCustomer",
    "quote",
    "serializedItem",
    "workOrderHistroy",
    "billHistroy",
    "assignDriverHistory",
    "workOrderHistroyCustomer",
    "billHistoryCustomer",
    "invoice",
    "inquiry",
    "driver",
    "email",
    "note",
    "noteEmail",
    "emailTemplate",
    "noteTemplate",
    "activityHistory",
    "activityHistoryProject",
    "text",
    "unit",
    "device",
    "playlist",
    "playlistGroup",
    "template",
    "media",
    "aiGeneration",
    "companyLogo",
    "dashboard",
    "signageUser",
    "signageActivity",
    "deviceCommand",
    "deviceHeartbeat",
    "deviceScreenshot",
    "billing",
    "appRelease",
    "deviceUpdate",
    "systemHealth",
    "deviceEvent",
    "pilotStatus",
    "menuSchedule",
    "holidaySuggestion",
    "posConnection",
    "posSyncRun",
    "posCatalog",
    "posMapping",
    "posDraft",
  ],
  endpoints: (builder) => ({
    getUser: builder.query<IApiResponse, void>({
      query: (request) => {
        return {
          url: "/user/userInfo",
          method: "POST",
          body: request,
        };
      },
      keepUnusedDataFor: 1,
      providesTags: ["User"],
    }),
    login: builder.mutation<IApiResponse, IUser>({
      query: (request) => {
        return {
          url: "/login",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
    }),
    createCategory: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/category",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["category", "productCategory"],
    }),
     createDuplicateCategory: builder.mutation<IApiResponse, Object>({
      query: (categoryId) => {
        return {
          url: `/category/${categoryId}/duplicate`,
          method: "POST",
        };
      },
      invalidatesTags: ["category", "productCategory"],
    }),
    reorderCategory: builder.mutation<IApiResponse, { orderedIds: number[] }>({
  query: (payload) => ({
    url: `/categories/reorder`,
    method: "POST",
    body: payload,
  }),
  invalidatesTags: ["category", "productCategory"], 
}),
   reorderProduct: builder.mutation<IApiResponse, {  categoryId: number, orderedIds: number[] }>({
  query: (payload) => ({
    url: `/products/reorder`,
    method: "POST",
    body: payload,
  }),
  invalidatesTags: ["product", "productCategory"], 
}),

      createModifierCategory: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/modifierCategory",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["modifierCategory", "productCategory"],
    }),
    createCompany: builder.mutation<IApiResponse, Object>({
      query: (request) => ({
        url: "/company",
        method: "POST",
        // Hand the raw object to fetchBaseQuery — it stringifies the
        // body itself and sets Content-Type from prepareHeaders. This
        // matches every other mutation in this file; the old manual
        // JSON.stringify sometimes interacted badly with browser
        // devtools / interceptors and surfaced as a generic catch.
        body: request,
      }),
      invalidatesTags: ["company"],
    }),
    createDepartment: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/department",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["department"],
    }),
    createCoursing: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/coursing",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["coursing"],
    }),
    createTax: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/tax",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["tax", "product", "category"],
    }),
    createProductBrand: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/product/brand",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productBrand", "product"],
    }),
    createProductTag: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/product/tags",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productTag", "product"],
    }),
    createProductCategory: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/product/category",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productCategory", "product"],
    }),
    createProduct: builder.mutation<IApiLoopingResponse, Object>({
      query: (request) => {
        return {
          url: "/product",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["product"],
    }),

    // Units
    getUnits: builder.query<IApiResponse, void>({
      query: () => ({ url: "/units", method: "GET" }),
      providesTags: ["unit"],
    }),
    createUnit: builder.mutation<IApiResponse, { name: string; abbreviation?: string }>({
      query: (request) => ({
        url: "/units",
        method: "POST",
        body: JSON.stringify(request),
      }),
      invalidatesTags: ["unit"],
    }),

    // Tax Groups
    getTaxGroups: builder.query<IApiResponse, void>({
      query: () => ({ url: "/tax-groups", method: "GET" }),
    }),

     createEvent: builder.mutation<IApiLoopingResponse, Object>({
      query: (request) => {
        return {
          url: "/event",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["event"],
    }),
       createVariant: builder.mutation<IApiLoopingResponse, Object>({
      query: (request) => {
        return {
          url: "/variant",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["product"],
    }),
      createModifier: builder.mutation<IApiLoopingResponse, Object>({
      query: (request) => {
        return {
          url: "/modifier",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["modifier"],
    }),
     createModifierGroup: builder.mutation<IApiLoopingResponse, Object>({
      query: (request) => {
        return {
          url: "/modifierGroup",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["modifierGroup"],
    }),
       createModifierGrp: builder.mutation<IApiLoopingResponse, Object>({
      query: (request) => {
        return {
          url: "/modifierGrp",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["modifierGrp"],
    }),
    createPrinter: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/printer",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["printer", "product"],
    }),
    getCategory: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/category/getAll",
          method: "POST",
        };
      },
      providesTags: ["product", "category"],
      keepUnusedDataFor: 0,
    }),
    getModifierCategory: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/modifierCategory/getAll",
          method: "POST",
        };
      },
      providesTags: ["product", "modifierCategory"],
      keepUnusedDataFor: 0,
    }),
    getSalesCategory: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/view-sales-categories",
          method: "POST",
        };
      },
      providesTags: ["product", "category"],
      keepUnusedDataFor: 0,
    }),
    getAllMainCategory: builder.query<IApiResponse, ICategoryPayload>({
      query: (request) => {
        return {
          url: "/main-category/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["product", "category"],
      keepUnusedDataFor: 0,
    }),
      getAllMainModifierCategory: builder.query<IApiResponse, ICategoryPayload>({
      query: (request) => {
        return {
          url: "/mainModifierCategory/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["product", "modifierCategory"],
      keepUnusedDataFor: 0,
    }),
    deleteCoursing: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/coursing/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["coursing"],
    }),
    deleteDepartment: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/department/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["department"],
    }),
    deletePrinter: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/printer/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["printer"],
    }),
    deleteEmailTemplate: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/email-template/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["emailTemplate"],
    }),
    deleteNoteTemplate: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/note-template/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["noteTemplate"],
    }),
    deleteProductBrand: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/product/brand/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["productBrand"],
    }),
    deleteProductTag: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/product/tags/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["productTag"],
    }),
    deleteTax: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/tax/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["tax"],
    }),
    deleteProductCategory: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/product/category/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["productCategory"],
    }),
    deleteCategory: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/category/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["category", "productCategory"],
    }),
       deleteModifierCategory: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/modifierCategory/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["modifierCategory", "productCategory"],
    }),
    getProduct: builder.query<IApiResponse, IProductPayload>({
      query: (request) => {
        return {
          url: "/product/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "product", "quote"],
      keepUnusedDataFor: 0,
    }),
       getEvent: builder.query<IApiResponse, IProductPayload>({
      query: (request) => {
        return {
          url: "/event/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "event", "quote"],
      keepUnusedDataFor: 0,
    }),
       getVariant: builder.query<IApiResponse, IVariantPayload>({
      query: (request) => {
        return {
          url: "/variant/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "product", "quote"],
      keepUnusedDataFor: 0,
    }),
    createGetAllProduct: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/product/getAll",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productTag", "product"],
    }),
    deleteProduct: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/product/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["product"],
    }),
     deleteEvent: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/event/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["event"],
    }),
       deleteVariant: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/variant/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["product"],
    }),
    getLastProductId: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/product/last-id",
          method: "GET",
        };
      },
      providesTags: ["product"],
      keepUnusedDataFor: 0,
    }),
    getNextProductBarcode: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/product/next-barcode",
          method: "GET",
        };
      },
      providesTags: ["product"],
      keepUnusedDataFor: 0,
    }),
     getModifier: builder.query<IApiResponse, IModifierPayload>({
      query: (request) => {
        return {
          url: "/modifier/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "modifier", "quote"],
      keepUnusedDataFor: 0,
    }),
    createGetAllModifier: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/modifier/getAll",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productTag", "modifier"],
    }),
    deleteModifier: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/modifier/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["modifier"],
    }),
    getModifierGroup: builder.query<IApiResponse,IModifierGroupPayload >({
      query: (request) => {
        return {
          url: "/modifierGroup/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "modifierGroup", "quote"],
      keepUnusedDataFor: 0,
    }),
      getModifierGrp: builder.query<IApiResponse,IModifierGroupPayload >({
      query: (request) => {
        return {
          url: "/modifierGrp/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "modifierGrp", "quote"],
      keepUnusedDataFor: 0,
    }),
    createGetAllModifierGroup: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/modifierGroup/getAll",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["productTag", "modifierGroup"],
    }),
    deleteModifierGroup: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/modifierGroup/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["modifierGroup"],
    }),
      deleteModifierGrp: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/modifierGrp/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["modifierGrp"],
    }),
   
    getDepartment: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/department/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["department", "category"],
      keepUnusedDataFor: 0,
    }),
    getTax: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/tax/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["tax", "category", "product"],
      keepUnusedDataFor: 0,
    }),
    getCoursing: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/coursing/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["coursing", "category"],
      keepUnusedDataFor: 0,
    }),
    getPrinter: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/printer/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["printer", "product"],
      keepUnusedDataFor: 0,
    }),
    getEmailTemplate: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/email-template/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["emailTemplate"],
      keepUnusedDataFor: 0,
    }),
    getNoteTemplate: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/note-template/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["noteTemplate"],
      keepUnusedDataFor: 0,
    }),
    getProductTag: builder.query<IApiResponse, ISearchIsActivePayload>({
      query: (request) => {
        return {
          url: "/product/tags/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productTag", "product"],
      keepUnusedDataFor: 0,
    }),
    getProductBrand: builder.query<IApiResponse, ISearchIsActivePayload>({
      query: (request) => {
        return {
          url: "/product/brand/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productBrand", "product"],
      keepUnusedDataFor: 0,
    }),
    getProductCategory: builder.query<IApiResponse, ISearchIsActivePayload>({
      query: (request) => {
        return {
          url: "/product/category/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productCategory", "product"],
      keepUnusedDataFor: 0,
    }),
    // getSubCategoryByCategoryId: builder.query<IApiResponse, ISearchIsActivePayload>({
    //   query: (request) => {
    //     return {
    //       url: '/product/category/getAll',
    //       method: "POST",
    //       body: request,
    //     };
    //   },
    //   providesTags:['productCategory','product'],
    //   keepUnusedDataFor: 0,
    // }),
    getSubCategoryByCategoryId: builder.query<
      IApiResponse,
      IGetByCategoryPayload
    >({
      query: (request) => {
        return {
          url: "/product/get-by-categories",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productGetByCategory", "product"],
      keepUnusedDataFor: 0,
    }),
    getSubCategoryByArray: builder.query<
      IApiResponse,
      IGetByCategoryArrayPayload
    >({
      query: (request) => {
        return {
          url: "/category/category-fiter/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productGetByCategory", "product", "productCategory"],
      keepUnusedDataFor: 0,
    }),
    getProductSubCategory: builder.query<IApiResponse, ISearchIsActivePayload>({
      query: (request) => {
        return {
          url: "/category/sub-category/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["productCategory", "product"],
      keepUnusedDataFor: 0,
    }),
    getAllCategoryName: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/all-category/getAll",
          method: "POST",
        };
      },
      providesTags: ["productCategory"],
      keepUnusedDataFor: 0,
    }),
     getAllModifierCategoryName: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/all-modifier-category/getAll",
          method: "POST",
        };
      },
      providesTags: ["productCategory"],
      keepUnusedDataFor: 0,
    }),
    getCtegoryFilter: builder.query<IApiResponse, ICategoryFilterPayload>({
      query: (request) => {
        return {
          url: "/category/filter",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["product"],
      keepUnusedDataFor: 0,
    }),
    getLoopingTable: builder.query<IApiResponse, ISearchPayload>({
      query: (request) => {
        return {
          url: "/looping/list-tables",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["category", "product", "looping"],
      keepUnusedDataFor: 0,
    }),
    createLoopingTable: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/looping/create-table",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["looping", "product"],
    }),
    getAllLoopingTable: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/looping/list-tables",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["looping", "product"],
      keepUnusedDataFor: 0,
    }),
    deleteLoopingTable: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/looping/delete-table",
          method: "DELETE",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["looping", "product"],
    }),
    updateLoopingTable: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/looping/update-table",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["looping", "product"],
    }),
    insertOrUpdateDataLoopingTable: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/looping/table/insert-update-data",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["product", "looping"],
    }),
    getCustomer: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/customer/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),
    getShop: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/shop/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["shop"],
      keepUnusedDataFor: 0,
    }),
    getRoute: builder.query<IApiResponse, ISearchRoutePayload>({
      query: (request) => {
        return {
          url: "/route/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["route"],
      keepUnusedDataFor: 0,
    }),
     getShopProduct: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/shop-product/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["shopProduct"],
      keepUnusedDataFor: 0,
    }),
     getVendor: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/vendor/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["vendor"],
      keepUnusedDataFor: 0,
    }),
    getProductSuppliers: builder.query<IApiResponse, string>({
      query: (productId) => ({
        url: `/product/${productId}/suppliers`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    getSupplierProducts: builder.query<IApiResponse, string>({
      query: (vendorId) => ({
        url: `/vendor/${vendorId}/products`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    getProductBarcodes: builder.query<IApiResponse, string>({
      query: (productId) => ({ url: `/product/${productId}/barcodes`, method: "GET" }),
      providesTags: ["product"],
      keepUnusedDataFor: 0,
    }),
    addProductBarcode: builder.mutation<IApiResponse, { productId: string; barcode: string; type: string; is_primary?: boolean }>({
      query: ({ productId, ...body }) => ({ url: `/product/${productId}/barcodes`, method: "POST", body: JSON.stringify(body) }),
      invalidatesTags: ["product"],
    }),
    setBarcodePrimary: builder.mutation<IApiResponse, string>({
      query: (id) => ({ url: `/product-barcodes/${id}/primary`, method: "PUT" }),
      invalidatesTags: ["product"],
    }),
    deleteBarcode: builder.mutation<IApiResponse, string>({
      query: (id) => ({ url: `/product-barcodes/${id}`, method: "DELETE" }),
      invalidatesTags: ["product"],
    }),
    scanBarcode: builder.query<IApiResponse, string>({
      query: (code) => ({ url: `/barcode/scan/${code}`, method: "GET" }),
    }),
    getProductHistory: builder.query<IApiResponse, string>({
      query: (productId) => ({
        url: `/product/${productId}/history`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    linkProductToSupplier: builder.mutation<IApiResponse, { vendorId: string; product_id: number; supplier_price: number; supplier_unit: string; unit_conversion?: string; is_default?: boolean }>({
      query: ({ vendorId, ...body }) => ({
        url: `/vendor/${vendorId}/products`,
        method: "POST",
        body: JSON.stringify(body),
      }),
    }),
      getInventory: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/inventory/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["inventory"],
      keepUnusedDataFor: 0,
    }),
    getSold: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/sold/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["sold"],
      keepUnusedDataFor: 0,
    }),
   
    getActivityHistory: builder.query<IActivityHistoryApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/customer/activityHistory/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["activityHistory"],
      keepUnusedDataFor: 0,
    }),

      getActivityHistoryVendor: builder.query<IActivityHistoryApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/vendor/activityHistory/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["activityHistory"],
      keepUnusedDataFor: 0,
    }),

    getProjectActivityHistory: builder.query<IActivityHistoryProjectApiResponse, ICustomerProjectPayload>({
      query: (request) => {
        return {
          url: "/customer/project/activityHistory/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["activityHistoryProject"],
      keepUnusedDataFor: 0,
    }),
     getInquiryActivityHistory: builder.query<IActivityHistoryProjectApiResponse, ICustomerProjectPayload>({
      query: (request) => {
        return {
          url: "/customer/inquiry/activityHistory/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["activityHistoryProject"],
      keepUnusedDataFor: 0,
    }),
    deleteCustomer: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/customer/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["customer"],
    }),
      deleteShop: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/shop/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["shop"],
    }),
     deleteRoute: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/route/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["route"],
    }),
     deleteVendor: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/vendor/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["vendor"],
    }),
     
    createCustomer: builder.mutation<IApiCustomerResponse, Object>({
      query: (request) => {
        return {
          url: "/customer",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
     createShop: builder.mutation<IApiCustomerResponse, Object>({
      query: (request) => {
        return {
          url: "/shop",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["shop"],
    }),
     createRoute: builder.mutation<IApiCustomerResponse, Object>({
      query: (request) => {
        return {
          url: "/route",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["route"],
    }),
      createShopProduct: builder.mutation<IApiShopProductResponse, { shopId: number; products: number[] }>({
  query: ({ shopId, products }) => ({
    url: `/shop-product/${shopId}`,
    method: "PUT",
    body: JSON.stringify({ products }),
  }),
  invalidatesTags: ["shopProduct"],
}),
    updateShopProductPrice: builder.mutation<IApiResponse, { shopId: number; productId: number; custom_price: number | null }>({
  query: ({ shopId, productId, custom_price }) => ({
    url: `/shop-product/${shopId}/${productId}/price`,
    method: "PUT",
    body: JSON.stringify({ custom_price }),
  }),
  invalidatesTags: ["shopProduct"],
}),
     createVendor: builder.mutation<IApiCustomerResponse, Object>({
      query: (request) => {
        return {
          url: "/vendor",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["vendor"],
    }),
   
    getCustomerTransaction: builder.query<
      IApiResponse,
      ICustomerTransactionPayload
    >({
      query: (request) => {
        return {
          url: "/customer/transaction/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),
    deleteCustomerTransaction: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/customer/transaction/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["customer"],
    }),
    createCustomerTransaction: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/customer/transaction",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
    getCompany: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/company/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["company"],
      keepUnusedDataFor: 0,
    }),
    deleteCompany: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/company/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["company"],
    }),
    getAllSearchCompany: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/company/search/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["company"],
      keepUnusedDataFor: 0,
    }),
    createWorkOrder: builder.mutation<IApiResponseWorkOrder, Object>({
      query: (request) => {
        return {
          url: "/work-order",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["quote"],
    }),
     createBill: builder.mutation<IApiResponseWorkOrder, Object>({
      query: (request) => {
        return {
          url: "/bill",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["bill"],
    }),
    editInvoice: builder.mutation<IApiResponseWorkOrder, Object>({
      query: (request) => {
        return {
          url: "/work-order/edit-invoice",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["quote"],
    }),
    getLastWorkOrderId: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/work-order/next-id",
          method: "GET",
        };
      },
      providesTags: ["quote"],
      keepUnusedDataFor: 0,
    }),
     getLastBillId: builder.query<IApiResponse, void>({
      query: () => {
        return {
          url: "/bill/next-id",
          method: "GET",
        };
      },
      providesTags: ["quote"],
      keepUnusedDataFor: 0,
    }),
    getWorkOrder: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/work-order/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["quote", "workOrderHistroy"],
      keepUnusedDataFor: 0,
    }),
       getBill: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/bill/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["bill"],
      keepUnusedDataFor: 0,
    }),
     getSerializedItem: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/serializedItem/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["serializedItem"],
      keepUnusedDataFor: 0,
    }),
    updateInvoiceStatus: builder.query<
      IApiResponse,
      IUpdateStatuInvoicePayload
    >({
      query: (request) => {
        return {
          url: "/work-order/update-invoice",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["workOrderHistroy"],
      keepUnusedDataFor: 0,
    }),
    getAllAssignDriverHistory: builder.query<
    IApiResponse,
    IAssignDriverPayload
  >({
    query: (request) => {
      return {
        url: "/assign-driver-history",
        method: "POST",
        body: request,
      };
    },
    providesTags: ["assignDriverHistory"],
    keepUnusedDataFor: 0,
  }),
    getAllInvoice: builder.query<IApiResponse, IInvoiceListPayload>({
      query: (request) => {
        return {
          url: "/invoice/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["invoice"],
      keepUnusedDataFor: 0,
    }),
       getAllInquiry: builder.query<IApiResponse, ICustomerProjectPayload>({
      query: (request) => {
        return {
          url: "/customer/inquiry/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["inquiry"],
      keepUnusedDataFor: 0,
    }),
     getAllBill: builder.query<IApiResponse, IInvoiceListPayload>({
      query: (request) => {
        return {
          url: "/bill/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["bill"],
      keepUnusedDataFor: 0,
    }),
    getAllInvoiceHistory: builder.query<
    IApiResponse,
    IInvoiceHistoryPayload
  >({
    query: (request) => {
      return {
        url: "/invoice-history",
        method: "POST",
        body: request,
      };
    },
    providesTags: ["invoiceHistroy"],
    keepUnusedDataFor: 0,
  }),

    getAllWorkOrderHistory: builder.query<
      IApiResponse,
      IWorkOrderHistoryPayload
    >({
      query: (request) => {
        return {
          url: "/work-order-history",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["workOrderHistroy"],
      keepUnusedDataFor: 0,
    }),
    getAllWorkOrderHistoryCustomer: builder.query<
    IApiResponse,
    IWorkOrderHistoryPayload
  >({
    query: (request) => {
      return {
        url: "/work-order-history-by-customer",
        method: "POST",
        body: request,
      };
    },
    providesTags: ["workOrderHistroyCustomer"],
    keepUnusedDataFor: 0,
  }),
   getAllBillHistory: builder.query<
      IApiResponse,
      IWorkOrderHistoryPayload
    >({
      query: (request) => {
        return {
          url: "/bill-history",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["billHistroy"],
      keepUnusedDataFor: 0,
    }),
    getAllBillHistoryCustomer: builder.query<
    IApiResponse,
    IWorkOrderHistoryPayload
  >({
    query: (request) => {
      return {
        url: "/bill-history-by-customer",
        method: "POST",
        body: request,
      };
    },
    providesTags: ["billHistoryCustomer"],
    keepUnusedDataFor: 0,
  }),
    createDriver: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/driver",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["driver"],
    }),
    getDriver: builder.query<IApiResponse, ILoopingTableListPayload>({
      query: (request) => {
        return {
          url: "/driver/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["driver"],
      keepUnusedDataFor: 0,
    }),
    deleteDriver: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/driver/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["driver"],
    }),
    getPriceHistory: builder.query<IApiResponse, IPriceHistoryPayload>({
      query: (request) => {
        return {
          url: "/work-order/price",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["quote"],
      keepUnusedDataFor: 0,
    }),
      getBillPriceHistory: builder.query<IApiResponse, IPriceHistoryPayload>({
      query: (request) => {
        return {
          url: "/bill/price",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["quote"],
      keepUnusedDataFor: 0,
    }),
    getCustomerProject: builder.query<IApiResponse,  ICustomerProjectPayload >({
      query: (request) => {
        return {
          url: "/customer/project/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),
    deleteCustomerProject: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/customer/project/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["customer"],
    }),
     deleteCustomerInquiry: builder.mutation<IApiResponse, string>({
      query: (Id) => {
        return {
          url: `/customer/inquiry/${Id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["customer"],
    }),
    createCustomerProject: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/customer/project",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
      createCustomerInquiry: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/customer/inquiry",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["inquiry"],
    }),
    getWorkOrderByCustomerProject: builder.query<
      IApiResponse,
      IWorkOrderByCustomerProjectPayload
    >({
      query: (request) => {
        return {
          url: "/work-order/project/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),
    getSelectorWorkOrderByCustomerProject: builder.query<IApiResponse, ISelectCustomerPayload>({
      query: (request) => {
        return {
          url: "/work-order/project/selector/getAll",
          method: "POST",
          body: request,
        };
      },
      providesTags: ["customer"],
      keepUnusedDataFor: 0,
    }),

    updateWorkOrderByCustomerProject: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/work-order/assign-project",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
    removedCustomerProjectM: builder.mutation<IApiResponse, Object>({
      query: (request) => {
        return {
          url: "/work-order/project/remove",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["customer"],
    }),
    createWorkOrderWithProject: builder.mutation<IApiResponseWorkOrder, Object>({
      query: (request) => {
        return {
          url: "/work-order/project-id",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["quote"],
    }),
    createEmail: builder.mutation<IEmail, Object>({
      query: (request) => {
        return {
          url: "/send-email",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email"],
    }),
     createCall: builder.mutation<IApiCustomerResponse, Object>({
      query: (request) => {
        return {
          url: "/save-call",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["email"],
    }),

    createEmailInvoice: builder.mutation<IApiResponseInvoice, FormData>({
      query: (formData) => {
        return {
          url: "/send-invoice-email",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["email"],
    }),

    createText: builder.mutation<IText, Object>({
      query: (request) => {
        return {
          url: "/send-text",
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      invalidatesTags: ["text"],
    }),
    
    
  
  createNote: builder.mutation<INote, Object>({
    query: (request) => {
      return {
        url: "/save-note",
        method: "POST",
        body: JSON.stringify(request),
      };
    },
    invalidatesTags: ["note"],
  }),

  createNoteEmail: builder.mutation<INoteEmail, Object>({
    query: (request) => {
      return {
        url: "/send-note-email",
        method: "POST",
        body: JSON.stringify(request),
      };
    },
    invalidatesTags: ["noteEmail"],
  }),

  createEmailTemplate: builder.mutation<IApiResponse, Object>({
    query: (request) => {
      return {
        url: "/email-template",
        method: "POST",
        body: JSON.stringify(request),
      };
    },
    invalidatesTags: ["emailTemplate"],
  }),
  createNoteTemplate: builder.mutation<IApiResponse, Object>({
    query: (request) => {
      return {
        url: "/note-template",
        method: "POST",
        body: JSON.stringify(request),
      };
    },
    invalidatesTags: ["noteTemplate"],
  }),
  getShopOrders: builder.query<{ success: boolean; data: IShopOrder[] }, number>({
    query: (shopId) => ({
      url: `/shop-orders/${shopId}`,
      method: "GET",
    }),
    providesTags: ["shopOrder"],
    keepUnusedDataFor: 0,
  }),
  createShopOrder: builder.mutation<IApiResponse, { shopId: number; items: any[]; total: number; notes?: string }>({
    query: ({ shopId, ...body }) => ({
      url: `/shop-orders/${shopId}`,
      method: "POST",
      body: JSON.stringify(body),
    }),
    invalidatesTags: ["shopOrder"],
  }),
  updateShopOrderStatus: builder.mutation<IApiResponse, { orderId: number; status: string }>({
    query: ({ orderId, status }) => ({
      url: `/shop-orders/${orderId}/status`,
      method: "PUT",
      body: JSON.stringify({ status }),
    }),
    invalidatesTags: ["shopOrder"],
  }),

  // Shop Invoices
  getShopInvoices: builder.query<{ success: boolean; data: any[] }, { searchText?: string; shop_id?: number; payment_status?: string }>({
    query: (body) => ({ url: `/shop-invoices/getAll`, method: "POST", body: JSON.stringify(body) }),
    providesTags: ["shopInvoice"],
    keepUnusedDataFor: 0,
  }),
  createShopInvoice: builder.mutation<any, { shop_order_id: number }>({
    query: (body) => ({ url: `/shop-invoices`, method: "POST", body: JSON.stringify(body) }),
    invalidatesTags: ["shopInvoice", "shopOrder"],
  }),
  getShopInvoice: builder.query<{ success: boolean; data: any }, number>({
    query: (id) => ({ url: `/shop-invoices/${id}`, method: "GET" }),
    providesTags: ["shopInvoice"],
  }),
  addShopInvoicePayment: builder.mutation<IApiResponse, { invoiceId: number; amount: number; payment_method: string; payment_date: string; notes?: string }>({
    query: ({ invoiceId, ...body }) => ({ url: `/shop-invoices/${invoiceId}/payment`, method: "POST", body: JSON.stringify(body) }),
    invalidatesTags: ["shopInvoice"],
  }),
  deleteShopInvoicePayment: builder.mutation<IApiResponse, number>({
    query: (paymentId) => ({ url: `/shop-invoice-payments/${paymentId}`, method: "DELETE" }),
    invalidatesTags: ["shopInvoice"],
  }),
  // ── Warehouse endpoints ──
  getWarehouseAlerts: builder.query<IApiResponse, void>({
    query: () => ({ url: "/warehouse/alerts", method: "GET" }),
    keepUnusedDataFor: 0,
  }),
  receiveStock: builder.mutation<IApiResponse, any>({
    query: (body) => ({ url: "/warehouse/receive-stock", method: "POST", body }),
    invalidatesTags: ["inventory"],
  }),
  getWarehouseForecastData: builder.mutation<IApiResponse, any>({
    query: (body) => ({ url: "/warehouse/forecast-data", method: "POST", body }),
  }),

  // ─── Digital Signage: Devices ──────────────────────────────────────
  getDevices: builder.query<IApiResponse, { searchText?: string; company_id?: number } | void>({
    query: (body) => ({ url: "/devices/getAll", method: "POST", body: body || {} }),
    providesTags: ["device"],
  }),
  getDevice: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}`, method: "GET" }),
    providesTags: ["device"],
  }),
  createDevice: builder.mutation<IApiResponse, Object>({
    query: (body) => ({ url: "/devices", method: "POST", body }),
    invalidatesTags: ["device"],
  }),
  // ─── Secure device activation ───────────────────────────────────────
  resendDeviceActivation: builder.mutation<IApiResponse, { id: number; email?: string }>({
    query: ({ id, ...body }) => ({ url: `/devices/${id}/resend-activation`, method: "POST", body }),
    invalidatesTags: ["device"],
  }),
  changeDeviceActivationEmail: builder.mutation<IApiResponse, { id: number; email: string; email_confirmation: string }>({
    query: ({ id, ...body }) => ({ url: `/devices/${id}/change-activation-email`, method: "POST", body }),
    invalidatesTags: ["device"],
  }),
  viewDeviceActivationCode: builder.mutation<IApiResponse, { id: number }>({
    query: ({ id }) => ({ url: `/devices/${id}/activation-code`, method: "GET" }),
  }),
  revokeDevice: builder.mutation<IApiResponse, { id: number }>({
    query: ({ id }) => ({ url: `/devices/${id}/revoke`, method: "POST" }),
    invalidatesTags: ["device"],
  }),
  rotateDeviceCredentials: builder.mutation<IApiResponse, { id: number }>({
    query: ({ id }) => ({ url: `/devices/${id}/rotate-credentials`, method: "POST" }),
    invalidatesTags: ["device"],
  }),
  claimDevicePairing: builder.mutation<IApiResponse, Object>({
    query: (body) => ({ url: "/devices/pair/claim", method: "POST", body }),
    invalidatesTags: ["device"],
  }),
  updateDevice: builder.mutation<IApiResponse, { id: number; body: Object }>({
    query: ({ id, body }) => ({ url: `/devices/${id}`, method: "PUT", body }),
    invalidatesTags: ["device"],
  }),
  deleteDevice: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}`, method: "DELETE" }),
    invalidatesTags: ["device"],
  }),
  assignDevicePlaylist: builder.mutation<
    IApiResponse,
    { id: number; playlist_id: number | null }
  >({
    query: ({ id, playlist_id }) => ({
      url: `/devices/${id}/assign-playlist`,
      method: "POST",
      body: { playlist_id },
    }),
    invalidatesTags: ["device"],
  }),

  // ─── Digital Signage: Playlists ────────────────────────────────────
  getPlaylists: builder.query<IApiResponse, { searchText?: string; company_id?: number } | void>({
    query: (body) => ({ url: "/playlists/getAll", method: "POST", body: body || {} }),
    providesTags: ["playlist"],
  }),
  getPlaylist: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/playlists/${id}`, method: "GET" }),
    providesTags: ["playlist"],
  }),
  createPlaylist: builder.mutation<IApiResponse, Object>({
    // Creating a playlist may also assign it to a group (playlist_group_id),
    // so refresh group views too.
    query: (body) => ({ url: "/playlists", method: "POST", body }),
    invalidatesTags: ["playlist", "playlistGroup"],
  }),
  updatePlaylist: builder.mutation<IApiResponse, { id: number; body: Object }>({
    // A group re-assignment (playlist_group_id) mutates the pivot, so the
    // Playlist Group page's "Assigned Playlists" must refetch as well.
    query: ({ id, body }) => ({ url: `/playlists/${id}`, method: "PUT", body }),
    invalidatesTags: ["playlist", "playlistGroup"],
  }),
  deletePlaylist: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/playlists/${id}`, method: "DELETE" }),
    invalidatesTags: ["playlist", "device"],
  }),
  addPlaylistItem: builder.mutation<
    IApiResponse,
    {
      id: number;
      // V1 — media item
      media_id?: number;
      // V2 — template item; requires duration
      template_id?: number;
      item_type?: "MEDIA" | "TEMPLATE";
      duration?: number | null;
      // Optional per-item transition applied at add-time.
      transition_in?: string | null;
      transition_out?: string | null;
      transition_duration_ms?: number | null;
      transition_easing?: string | null;
      use_playlist_default_transition?: boolean;
    }
  >({
    query: ({ id, ...body }) => ({
      url: `/playlists/${id}/items`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["playlist"],
  }),
  // Update an existing item's duration and/or transition in place
  // (replaces the old remove+re-add for duration edits).
  updatePlaylistItem: builder.mutation<
    IApiResponse,
    { playlistId: number; itemId: number; body: Object }
  >({
    query: ({ playlistId, itemId, body }) => ({
      url: `/playlists/${playlistId}/items/${itemId}`,
      method: "PUT",
      body,
    }),
    invalidatesTags: ["playlist"],
  }),
  // "Apply to all items": optionally persist the playlist default, then
  // make every item inherit it.
  applyPlaylistDefaultTransition: builder.mutation<
    IApiResponse,
    { id: number; body?: Object }
  >({
    query: ({ id, body }) => ({
      url: `/playlists/${id}/items/apply-default-transition`,
      method: "POST",
      body: body || {},
    }),
    invalidatesTags: ["playlist"],
  }),
  removePlaylistItem: builder.mutation<
    IApiResponse,
    { playlistId: number; itemId: number }
  >({
    query: ({ playlistId, itemId }) => ({
      url: `/playlists/${playlistId}/items/${itemId}`,
      method: "DELETE",
    }),
    invalidatesTags: ["playlist"],
  }),
  reorderPlaylistItems: builder.mutation<
    IApiResponse,
    { id: number; item_ids: number[] }
  >({
    query: ({ id, item_ids }) => ({
      url: `/playlists/${id}/items/reorder`,
      method: "POST",
      body: { item_ids },
    }),
    invalidatesTags: ["playlist"],
  }),

  // ─── Digital Signage: Templates (V2) ───────────────────────────────
  getTemplates: builder.query<IApiResponse, { searchText?: string; company_id?: number } | void>({
    query: (body) => ({ url: "/templates/getAll", method: "POST", body: body || {} }),
    providesTags: ["template"],
  }),
  getTemplate: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/templates/${id}`, method: "GET" }),
    providesTags: ["template"],
  }),
  createTemplate: builder.mutation<IApiResponse, Object>({
    query: (body) => ({ url: "/templates", method: "POST", body }),
    invalidatesTags: ["template"],
  }),
  updateTemplate: builder.mutation<IApiResponse, { id: number; body: Object }>({
    query: ({ id, body }) => ({ url: `/templates/${id}`, method: "PUT", body }),
    invalidatesTags: ["template", "playlist"],
  }),
  deleteTemplate: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/templates/${id}`, method: "DELETE" }),
    invalidatesTags: ["template", "playlist"],
  }),
  replaceTemplateZones: builder.mutation<
    IApiResponse,
    {
      id: number;
      zones: Array<{
        name?: string | null;
        x: number; y: number; w: number; h: number;
        z_order?: number;
        widget_type: string;
        widget_config?: Record<string, unknown> | null;
      }>;
    }
  >({
    query: ({ id, zones }) => ({
      url: `/templates/${id}/zones`,
      method: "POST",
      body: { zones },
    }),
    invalidatesTags: ["template", "playlist"],
  }),

  // ─── Digital Signage: Media ────────────────────────────────────────
  getMedia: builder.query<
    IApiResponse,
    { searchText?: string; type?: "image" | "video"; company_id?: number } | void
  >({
    query: (body) => ({ url: "/media/getAll", method: "POST", body: body || {} }),
    providesTags: ["media"],
  }),
  deleteMedia: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/media/${id}`, method: "DELETE" }),
    invalidatesTags: ["media", "playlist"],
  }),
  bulkDeleteMedia: builder.mutation<IApiResponse, { media_ids: number[] }>({
    query: (body) => ({ url: "/media/bulk-delete", method: "POST", body }),
    invalidatesTags: ["media", "playlist"],
  }),

  // ─── Digital Signage: AI image generation ──────────────────────────
  generateAiImage: builder.mutation<
    IApiResponse,
    {
      prompt: string;
      aspect_ratio?: "landscape" | "portrait" | "square";
      style?: string;
      negative?: string;
      company_id?: number;
      // Company-logo compositing (logo file resolved server-side).
      include_logo?: boolean;
      logo_position?:
        | "template" | "ai" | "top-left" | "top-center" | "top-right"
        | "bottom-left" | "bottom-center" | "bottom-right";
      logo_size?: "small" | "medium" | "large";
      logo_background?: "none" | "white" | "dark" | "panel";
      // Structured promo fields (kept for editable layers, not baked in).
      headline?: string;
      regular_price?: string;
      sale_price?: string;
      promo_start?: string;
      promo_end?: string;
      cta?: string;
      disclaimer?: string;
      // Generation purpose + product accuracy.
      generation_purpose?: "digital_signage" | "social_media" | "print_flyer" | "general";
      improved_prompt?: string;
      baked_text?: boolean;
      bake_company_name?: boolean;
      product_name?: string;
      product_brand?: string;
      product_model?: string;
      product_reference_media_id?: number;
      product_accuracy_mode?: "approximation" | "exact_compositing" | "reference_guided";
      preserve_product?: boolean;
      product_position?:
        | "template" | "ai" | "left" | "center" | "right"
        | "top-left" | "top-center" | "top-right"
        | "bottom-left" | "bottom-center" | "bottom-right";
      product_size?: "small" | "medium" | "large" | "hero";
      product_background_mode?: "preserve" | "remove" | "transparency";
      product_shadow?: "off" | "soft" | "strong";
      product_reflection?: "none" | "subtle" | "visible";
      product_lighting_integration?: "off" | "automatic";
      // Template/layout zone inheritance (zones resolved server-side).
      template_id?: number;
      use_template_zones?: boolean;
      template_version?: number;
      override_template_conflict?: boolean;
    }
  >({
    query: (body) => ({ url: "/ai/generate-image", method: "POST", body }),
    // A generation lands a new asset in the library → refresh both.
    invalidatesTags: ["media", "aiGeneration"],
  }),
  // Availability + preview URL for the selected company's saved logo. Resolved
  // server-side; the client never supplies the logo file.
  getCompanyLogo: builder.query<IApiResponse, { company_id?: number } | void>({
    query: (params) => ({ url: "/ai/company-logo", method: "GET", params: params || {} }),
    providesTags: ["companyLogo"],
  }),
  // Upload/replace the company's saved logo (multipart). Persists onto the
  // company so it's reused everywhere. Excluded from the JSON content-type in
  // prepareHeaders so the browser sets the multipart boundary.
  uploadCompanyLogo: builder.mutation<IApiResponse, FormData>({
    query: (body) => ({ url: "/ai/company-logo", method: "POST", body }),
    invalidatesTags: ["companyLogo"],
  }),
  getAiGenerations: builder.query<IApiResponse, { company_id?: number } | void>({
    query: (body) => ({ url: "/ai/generations", method: "GET", params: body || {} }),
    providesTags: ["aiGeneration"],
  }),
  // "Improve Prompt with AI" — returns an improved DRAFT prompt only (never
  // persists). The modal shows it for the user to accept or edit.
  improveImagePrompt: builder.mutation<
    IApiResponse,
    {
      prompt: string;
      style?: string;
      aspect_ratio?: string;
      // Structured logo intent so the rewrite reserves space for the official
      // logo instead of forbidding all logos.
      include_logo?: boolean;
      bake_company_name?: boolean;
      logo_position?: string;
      logo_size?: string;
      logo_background?: string;
      product_accuracy_mode?: "approximation" | "exact_compositing" | "reference_guided";
      product_name?: string;
      product_brand?: string;
      product_model?: string;
    }
  >({
    query: (body) => ({ url: "/ai/improve-image-prompt", method: "POST", body }),
  }),
  // Build the final provider prompt for preview only — no generation, no cost.
  previewImagePrompt: builder.mutation<IApiResponse, Record<string, any>>({
    query: (body) => ({ url: "/ai/preview-prompt", method: "POST", body }),
  }),
  // Normalized AI layout zones for a template (server-authorized preview). Used
  // when the dialog inherits placement from a selected layout.
  getTemplateZones: builder.query<
    IApiResponse,
    { template_id: number; aspect_ratio?: string; template_version?: number; company_id?: number }
  >({
    query: (params) => ({ url: "/ai/template-zones", method: "GET", params }),
  }),

  // ─── Digital Signage: AI template assistant (feature-flagged) ──────
  // Proposes a validated template structure only — the server never persists
  // it. The designer applies it through the normal save flow after review.
  generateAiTemplate: builder.mutation<
    IApiResponse,
    { prompt: string; orientation: "landscape" | "portrait"; business_type?: string }
  >({
    query: (body) => ({ url: "/ai/generate-template", method: "POST", body }),
  }),

  // ─── Digital Signage: Playlist Groups ──────────────────────────────
  getPlaylistGroups: builder.query<IApiResponse, { searchText?: string; company_id?: number } | void>({
    query: (body) => ({ url: "/playlist-groups/getAll", method: "POST", body: body || {} }),
    providesTags: ["playlistGroup"],
  }),
  getPlaylistGroup: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/playlist-groups/${id}`, method: "GET" }),
    providesTags: ["playlistGroup"],
  }),
  createPlaylistGroup: builder.mutation<IApiResponse, Object>({
    query: (body) => ({ url: "/playlist-groups", method: "POST", body }),
    invalidatesTags: ["playlistGroup", "dashboard"],
  }),
  updatePlaylistGroup: builder.mutation<IApiResponse, { id: number; body: Object }>({
    query: ({ id, body }) => ({ url: `/playlist-groups/${id}`, method: "PUT", body }),
    invalidatesTags: ["playlistGroup", "dashboard"],
  }),
  deletePlaylistGroup: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/playlist-groups/${id}`, method: "DELETE" }),
    invalidatesTags: ["playlistGroup", "device", "dashboard"],
  }),
  addPlaylistToGroup: builder.mutation<
    IApiResponse,
    {
      id: number; playlist_id: number; duration_minutes?: number | null; is_active?: boolean;
      transition_to_next?: string | null;
      transition_duration_ms?: number | null;
      transition_easing?: string | null;
      use_group_default_transition?: boolean;
      // Scheduling fields (validated by AddPlaylistToGroupRequest) — used by the
      // guided "Create Menu Schedule" flow to schedule the playlist in one call.
      start_date?: string | null;
      end_date?: string | null;
      start_time?: string | null;
      end_time?: string | null;
      days_of_week?: number | null;
      priority?: number | null;
      force?: boolean;
    }
  >({
    query: ({ id, ...body }) => ({
      url: `/playlist-groups/${id}/items`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["playlistGroup"],
  }),
  // "Apply to all assigned playlists": optionally persist the group
  // default, then make every assignment inherit it.
  applyGroupDefaultTransition: builder.mutation<
    IApiResponse,
    { id: number; body?: Object }
  >({
    query: ({ id, body }) => ({
      url: `/playlist-groups/${id}/items/apply-default-transition`,
      method: "POST",
      body: body || {},
    }),
    invalidatesTags: ["playlistGroup"],
  }),
  updatePlaylistGroupItem: builder.mutation<
    IApiResponse,
    { groupId: number; itemId: number; body: Object }
  >({
    query: ({ groupId, itemId, body }) => ({
      url: `/playlist-groups/${groupId}/items/${itemId}`,
      method: "PUT",
      body,
    }),
    invalidatesTags: ["playlistGroup"],
  }),
  removePlaylistFromGroup: builder.mutation<
    IApiResponse,
    { groupId: number; itemId: number }
  >({
    query: ({ groupId, itemId }) => ({
      url: `/playlist-groups/${groupId}/items/${itemId}`,
      method: "DELETE",
    }),
    invalidatesTags: ["playlistGroup"],
  }),
  reorderPlaylistGroupItems: builder.mutation<
    IApiResponse,
    { id: number; item_ids: number[] }
  >({
    query: ({ id, item_ids }) => ({
      url: `/playlist-groups/${id}/items/reorder`,
      method: "POST",
      body: { item_ids },
    }),
    invalidatesTags: ["playlistGroup"],
  }),
  assignDevicePlaylistGroup: builder.mutation<
    IApiResponse,
    { id: number; playlist_group_id: number | null }
  >({
    query: ({ id, playlist_group_id }) => ({
      url: `/devices/${id}/assign-playlist-group`,
      method: "POST",
      body: { playlist_group_id },
    }),
    invalidatesTags: ["device", "playlistGroup"],
  }),
  // Onboarding wrap-up: one transactional call that links the playlist to the
  // group AND assigns the group to the device, returning the hydrated device
  // so the client can verify playlist_group_id actually persisted before it
  // reports success and redirects. Reuses the same backend services as the
  // Device View "Apply" and the Playlist Group page.
  finalizeOnboarding: builder.mutation<
    IApiResponse,
    { id: number; playlist_group_id: number | null; playlist_id: number | null }
  >({
    query: ({ id, ...body }) => ({
      url: `/devices/${id}/finalize-onboarding`,
      method: "POST",
      body,
    }),
    invalidatesTags: ["device", "playlistGroup", "playlist"],
  }),

  // ─── Device Monitoring (admin) ─────────────────────────────────────
  disableDevice: builder.mutation<IApiResponse, { id: number; reason?: string }>({
    query: ({ id, reason }) => ({
      url: `/devices/${id}/disable`, method: "POST", body: { reason: reason ?? null },
    }),
    invalidatesTags: ["device", "deviceCommand", "signageActivity"],
  }),
  enableDevice: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/enable`, method: "POST" }),
    invalidatesTags: ["device", "deviceCommand", "signageActivity"],
  }),
  forceRefreshDevice: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/force-refresh`, method: "POST" }),
    invalidatesTags: ["device", "deviceCommand", "signageActivity"],
  }),
  queueDeviceCommand: builder.mutation<
    IApiResponse,
    { id: number; command: string; payload?: Record<string, unknown> }
  >({
    query: ({ id, command, payload }) => ({
      url: `/devices/${id}/commands`,
      method: "POST",
      body: { command, payload: payload ?? {} },
    }),
    invalidatesTags: ["deviceCommand", "device", "signageActivity"],
  }),
  getDeviceCommands: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/commands`, method: "GET" }),
    providesTags: ["deviceCommand"],
  }),
  getDeviceHeartbeats: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/heartbeats`, method: "GET" }),
    providesTags: ["deviceHeartbeat"],
  }),
  getDeviceScreenshot: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/screenshot`, method: "GET" }),
    providesTags: ["deviceScreenshot"],
  }),

  // ─── Billing / SaaS controls (super admin only) ─────────────────────
  getBillingReport: builder.query<IApiResponse, { status?: string } | void>({
    query: (body) => {
      const params = new URLSearchParams();
      if (body && (body as any).status) params.set("status", String((body as any).status));
      const qs = params.toString();
      return { url: `/billing/devices${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["billing", "company", "device"],
  }),
  updateCompanyPlan: builder.mutation<
    IApiResponse,
    {
      id: number;
      subscription_plan?: string | null;
      max_devices?: number | null;
      billing_email?: string | null;
      billing_notes?: string | null;
    }
  >({
    query: ({ id, ...body }) => ({
      url: `/billing/companies/${id}/plan`, method: "POST", body,
    }),
    invalidatesTags: ["billing", "company", "signageActivity"],
  }),
  extendTrial: builder.mutation<IApiResponse, { id: number; days: number }>({
    query: ({ id, days }) => ({
      url: `/billing/companies/${id}/extend-trial`, method: "POST", body: { days },
    }),
    invalidatesTags: ["billing", "company", "signageActivity"],
  }),
  suspendCompany: builder.mutation<IApiResponse, { id: number; reason?: string }>({
    query: ({ id, reason }) => ({
      url: `/billing/companies/${id}/suspend`, method: "POST", body: { reason: reason ?? null },
    }),
    invalidatesTags: ["billing", "company", "device", "signageActivity"],
  }),
  reactivateCompany: builder.mutation<IApiResponse, { id: number; status?: "active" | "trial" }>({
    query: ({ id, status }) => ({
      url: `/billing/companies/${id}/reactivate`, method: "POST",
      body: { status: status ?? "active" },
    }),
    invalidatesTags: ["billing", "company", "device", "signageActivity"],
  }),
  updateDeviceLicense: builder.mutation<
    IApiResponse,
    {
      id: number;
      license_status?: "active" | "suspended" | "expired";
      billing_enabled?: boolean;
      monthly_rate?: number | null;
      license_expires_at?: string | null;
    }
  >({
    query: ({ id, ...body }) => ({
      url: `/billing/devices/${id}/license`, method: "POST", body,
    }),
    invalidatesTags: ["billing", "device", "signageActivity"],
  }),

  // ─── App Releases (super admin) ─────────────────────────────────────
  getAppReleases: builder.query<
    IApiResponse,
    { platform?: string; channel?: string; include_inactive?: boolean } | void
  >({
    query: (body) => {
      const params = new URLSearchParams();
      const b = (body || {}) as any;
      if (b.platform) params.set("platform", String(b.platform));
      if (b.channel) params.set("channel", String(b.channel));
      if (b.include_inactive) params.set("include_inactive", "1");
      const qs = params.toString();
      return { url: `/app-releases${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["appRelease"],
  }),
  uploadAppRelease: builder.mutation<IApiResponse, FormData>({
    query: (form) => ({ url: "/app-releases", method: "POST", body: form }),
    invalidatesTags: ["appRelease", "signageActivity"],
  }),
  updateAppRelease: builder.mutation<
    IApiResponse,
    {
      id: number;
      release_channel?: "stable" | "beta";
      release_notes?: string;
      is_active?: boolean;
      force_update?: boolean;
    }
  >({
    query: ({ id, ...body }) => ({
      url: `/app-releases/${id}`, method: "PATCH", body,
    }),
    invalidatesTags: ["appRelease", "signageActivity"],
  }),
  deleteAppRelease: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/app-releases/${id}`, method: "DELETE" }),
    invalidatesTags: ["appRelease", "signageActivity"],
  }),
  // Full release-details payload (metadata + rollout stats + deployments).
  getAppRelease: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/app-releases/${id}`, method: "GET" }),
    providesTags: ["appRelease", "deviceUpdate"],
  }),
  // Companies + device groups with active-device counts, for "Deploy To".
  getDeployTargets: builder.query<IApiResponse, void>({
    query: () => ({ url: `/app-releases/deploy-targets`, method: "GET" }),
    providesTags: ["appRelease"],
  }),
  // Parse an APK (no store) to show detected metadata in the wizard.
  inspectAppRelease: builder.mutation<IApiResponse, FormData>({
    query: (form) => ({ url: "/app-releases/inspect", method: "POST", body: form }),
  }),
  // Deploy / expand a release to more targets.
  deployAppRelease: builder.mutation<IApiResponse, { id: number; body: any }>({
    query: ({ id, body }) => ({ url: `/app-releases/${id}/deploy`, method: "POST", body }),
    invalidatesTags: ["appRelease", "device", "deviceUpdate", "signageActivity"],
  }),
  pauseAppRelease: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/app-releases/${id}/pause`, method: "POST" }),
    invalidatesTags: ["appRelease", "device", "deviceUpdate", "signageActivity"],
  }),
  resumeAppRelease: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/app-releases/${id}/resume`, method: "POST" }),
    invalidatesTags: ["appRelease", "device", "deviceUpdate", "signageActivity"],
  }),
  retryFailedAppRelease: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/app-releases/${id}/retry-failed`, method: "POST" }),
    invalidatesTags: ["appRelease", "device", "deviceUpdate", "signageActivity"],
  }),
  deactivateAppRelease: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/app-releases/${id}/deactivate`, method: "POST" }),
    invalidatesTags: ["appRelease", "device", "deviceUpdate", "signageActivity"],
  }),

  // ─── Per-device app-update lifecycle ────────────────────────────────
  getDeviceAvailableReleases: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/available-releases`, method: "GET" }),
    providesTags: ["deviceUpdate"],
  }),
  getDeviceUpdateHistory: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/update-history`, method: "GET" }),
    providesTags: ["deviceUpdate"],
  }),
  assignDeviceRelease: builder.mutation<IApiResponse, { id: number; body: any }>({
    query: ({ id, body }) => ({ url: `/devices/${id}/assign-release`, method: "POST", body }),
    invalidatesTags: ["device", "deviceUpdate", "signageActivity"],
  }),
  installDeviceUpdate: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/install-update`, method: "POST" }),
    invalidatesTags: ["device", "deviceUpdate", "signageActivity"],
  }),
  approveDeviceUpdate: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/approve-update`, method: "POST" }),
    invalidatesTags: ["device", "deviceUpdate", "signageActivity"],
  }),
  scheduleDeviceUpdate: builder.mutation<IApiResponse, { id: number; body: any }>({
    query: ({ id, body }) => ({ url: `/devices/${id}/schedule-update`, method: "POST", body }),
    invalidatesTags: ["device", "deviceUpdate", "signageActivity"],
  }),
  pauseDeviceUpdate: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/pause-update`, method: "POST" }),
    invalidatesTags: ["device", "deviceUpdate", "signageActivity"],
  }),
  retryDeviceUpdate: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/retry-update`, method: "POST" }),
    invalidatesTags: ["device", "deviceUpdate", "signageActivity"],
  }),
  rollbackDevice: builder.mutation<IApiResponse, { id: number; release_id: number }>({
    query: ({ id, release_id }) => ({
      url: `/devices/${id}/rollback`, method: "POST", body: { release_id },
    }),
    invalidatesTags: ["device", "deviceUpdate", "signageActivity"],
  }),

  // ─── System diagnostics (super admin only) ─────────────────────────
  getSystemHealth: builder.query<IApiResponse, void>({
    query: () => ({ url: "/system/health", method: "GET" }),
    providesTags: ["systemHealth"],
  }),
  getSystemChecklist: builder.query<IApiResponse, void>({
    query: () => ({ url: "/system/checklist", method: "GET" }),
    providesTags: ["systemHealth"],
  }),
  getSystemLogs: builder.query<
    IApiResponse,
    { level?: string; limit?: number } | void
  >({
    query: (body) => {
      const params = new URLSearchParams();
      const b = (body || {}) as any;
      if (b.level) params.set("level", String(b.level));
      if (b.limit) params.set("limit", String(b.limit));
      const qs = params.toString();
      return { url: `/system/logs${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["systemHealth"],
  }),

  // ─── Pilot QA checklist (super admin) ──────────────────────────────
  getPilotChecklist: builder.query<IApiResponse, void>({
    query: () => ({ url: "/system/pilot-checklist", method: "GET" }),
    providesTags: ["systemHealth"],
  }),
  setPilotChecklistItem: builder.mutation<IApiResponse, { id: string; checked: boolean }>({
    query: (body) => ({ url: "/system/pilot-checklist", method: "PUT", body }),
    invalidatesTags: ["systemHealth"],
  }),

  // ─── Installer PIN (super admin) ───────────────────────────────────
  generateInstallerPin: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/companies/${id}/installer-pin`, method: "POST" }),
    invalidatesTags: ["company"],
  }),
  revokeInstallerPin: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/companies/${id}/installer-pin`, method: "DELETE" }),
    invalidatesTags: ["company"],
  }),
  // Public — no auth header attached by the interceptor for these.
  getInstallerByPin: builder.query<IApiResponse, string>({
    query: (pin) => ({ url: `/installer/${pin}`, method: "GET" }),
  }),
  getLatestAndroidRelease: builder.query<IApiResponse, void>({
    query: () => ({ url: "/download/android", method: "GET" }),
  }),

  // ─── Device events (stability log) ─────────────────────────────────
  getDeviceEvents: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/devices/${id}/events`, method: "GET" }),
    providesTags: ["deviceEvent"],
  }),

  // ─── Pilot status dashboard (super admin) ──────────────────────────
  getPilotStatus: builder.query<IApiResponse, void>({
    query: () => ({ url: "/pilot/status", method: "GET" }),
    providesTags: ["pilotStatus"],
  }),

  // ─── Digital Signage: Dashboard ────────────────────────────────────
  getDashboardSummary: builder.query<IApiResponse, void>({
    query: () => ({ url: "/dashboard/summary", method: "GET" }),
    providesTags: ["dashboard"],
    // Aggregates from device/playlist/media tables — recompute when any change.
    keepUnusedDataFor: 30,
  }),

  // ─── Digital Signage: Tenant Users ─────────────────────────────────
  getSignageUsers: builder.query<IApiResponse, { searchText?: string; company_id?: number } | void>({
    query: (body) => {
      const params = new URLSearchParams();
      if (body && (body as any).searchText) params.set("searchText", String((body as any).searchText));
      if (body && (body as any).company_id) params.set("company_id", String((body as any).company_id));
      const qs = params.toString();
      return { url: `/signage-users${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["signageUser"],
  }),
  createSignageUser: builder.mutation<IApiResponse, Object>({
    query: (body) => ({ url: "/signage-users", method: "POST", body }),
    invalidatesTags: ["signageUser", "signageActivity"],
  }),
  updateSignageUser: builder.mutation<IApiResponse, { id: number; body: Object }>({
    query: ({ id, body }) => ({ url: `/signage-users/${id}`, method: "PUT", body }),
    invalidatesTags: ["signageUser", "signageActivity"],
  }),
  deleteSignageUser: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/signage-users/${id}`, method: "DELETE" }),
    invalidatesTags: ["signageUser", "signageActivity"],
  }),

  // ─── Digital Signage: Activity feed ─────────────────────────────────
  getSignageActivity: builder.query<
    IApiResponse,
    { company_id?: number; action?: string; subject_type?: string; per_page?: number; page?: number } | void
  >({
    query: (body) => {
      const params = new URLSearchParams();
      const b = (body || {}) as any;
      if (b.company_id) params.set("company_id", String(b.company_id));
      if (b.action) params.set("action", String(b.action));
      if (b.subject_type) params.set("subject_type", String(b.subject_type));
      if (b.per_page) params.set("per_page", String(b.per_page));
      if (b.page) params.set("page", String(b.page));
      const qs = params.toString();
      return { url: `/activity${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["signageActivity"],
  }),

  // ─── Smart Menu Scheduling ─────────────────────────────────────────
  // Company-wide overview of every scheduled menu (read-only aggregation of
  // playlist-group items across the authorized groups).
  getMenuSchedules: builder.query<
    IApiResponse,
    { company_id?: number } | void
  >({
    query: (body) => {
      const params = new URLSearchParams();
      const b = (body || {}) as any;
      if (b.company_id) params.set("company_id", String(b.company_id));
      const qs = params.toString();
      return { url: `/menu-schedules${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["menuSchedule", "playlistGroup"],
  }),
  checkScheduleConflicts: builder.mutation<
    IApiResponse,
    { groupId: number; body: Object }
  >({
    query: ({ groupId, body }) => ({
      url: `/playlist-groups/${groupId}/items/check-conflicts`,
      method: "POST",
      body,
    }),
  }),
  previewSchedule: builder.query<
    IApiResponse,
    { groupId: number; at?: string; device_id?: number }
  >({
    query: ({ groupId, at, device_id }) => {
      const params = new URLSearchParams();
      if (at) params.set("at", at);
      if (device_id) params.set("device_id", String(device_id));
      const qs = params.toString();
      return { url: `/playlist-groups/${groupId}/preview${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["menuSchedule"],
  }),
  getHolidaySuggestions: builder.query<
    IApiResponse,
    { year?: number; province?: string } | void
  >({
    query: (body) => {
      const params = new URLSearchParams();
      const b = (body || {}) as any;
      if (b.year) params.set("year", String(b.year));
      if (b.province) params.set("province", String(b.province));
      const qs = params.toString();
      return { url: `/holidays/suggestions${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["holidaySuggestion"],
  }),
  publishPlaylist: builder.mutation<IApiResponse, { id: number; label?: string }>({
    query: ({ id, label }) => ({ url: `/playlists/${id}/publish`, method: "POST", body: { label } }),
    invalidatesTags: ["playlist", "signageActivity"],
  }),
  getPlaylistVersions: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/playlists/${id}/versions`, method: "GET" }),
    providesTags: ["playlist"],
  }),
  rollbackPlaylist: builder.mutation<IApiResponse, { id: number; version: number }>({
    query: ({ id, version }) => ({ url: `/playlists/${id}/rollback`, method: "POST", body: { version } }),
    invalidatesTags: ["playlist", "signageActivity"],
  }),
  createEmergencyOverride: builder.mutation<IApiResponse, Object>({
    query: (body) => ({ url: "/emergency-overrides", method: "POST", body }),
    invalidatesTags: ["menuSchedule", "device"],
  }),
  deactivateEmergencyOverride: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/emergency-overrides/${id}/deactivate`, method: "POST" }),
    invalidatesTags: ["menuSchedule", "device"],
  }),
  getEmergencyOverrides: builder.query<
    IApiResponse,
    { company_id?: number; is_active?: boolean } | void
  >({
    query: (body) => {
      const params = new URLSearchParams();
      const b = (body || {}) as any;
      if (b.company_id) params.set("company_id", String(b.company_id));
      if (b.is_active !== undefined) params.set("is_active", b.is_active ? "1" : "0");
      const qs = params.toString();
      return { url: `/emergency-overrides${qs ? `?${qs}` : ""}`, method: "GET" };
    },
    providesTags: ["menuSchedule"],
  }),
  // AI menu-copy assistant — returns a draft suggestion; never persists.
  improveMenuCopy: builder.mutation<
    IApiResponse,
    { text: string; mode: "fix_spelling" | "shorten" | "rewrite_for_screen" | "translate"; target_language?: string }
  >({
    query: (body) => ({ url: "/ai/improve-menu-copy", method: "POST", body }),
  }),

  /* ---------------- POS / Clover menu sync ---------------- */
  getPosConnections: builder.query<IApiResponse, { company_id?: number } | void>({
    query: (body) => ({ url: "/pos/connections/getAll", method: "POST", body: body || {} }),
    providesTags: ["posConnection"],
  }),
  getPosConnection: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/pos/connections/${id}`, method: "GET" }),
    providesTags: ["posConnection"],
  }),
  startPosOauth: builder.mutation<IApiResponse, { company_id?: number; environment?: string }>({
    query: (body) => ({ url: "/pos/oauth/start", method: "POST", body }),
  }),
  exchangePosOauth: builder.mutation<IApiResponse, { state: string; code: string; merchant_id: string }>({
    query: (body) => ({ url: "/pos/oauth/exchange", method: "POST", body }),
    invalidatesTags: ["posConnection", "signageActivity"],
  }),
  disconnectPos: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/pos/connections/${id}/disconnect`, method: "POST" }),
    invalidatesTags: ["posConnection", "signageActivity"],
  }),
  updatePosSettings: builder.mutation<IApiResponse, { id: number; body: Object }>({
    query: ({ id, body }) => ({ url: `/pos/connections/${id}/settings`, method: "POST", body }),
    invalidatesTags: ["posConnection"],
  }),
  importPosMenu: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/pos/connections/${id}/import`, method: "POST" }),
    invalidatesTags: ["posConnection", "posSyncRun"],
  }),
  syncPosNow: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/pos/connections/${id}/sync-now`, method: "POST" }),
    invalidatesTags: ["posConnection", "posSyncRun"],
  }),
  reconcilePos: builder.mutation<IApiResponse, number>({
    query: (id) => ({ url: `/pos/connections/${id}/reconcile`, method: "POST" }),
    invalidatesTags: ["posConnection", "posSyncRun"],
  }),
  getPosCategories: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/pos/connections/${id}/categories/getAll`, method: "POST", body: {} }),
    providesTags: ["posCatalog"],
  }),
  getPosItems: builder.query<
    IApiResponse,
    { id: number; pos_category_id?: number; searchText?: string; unavailable_only?: boolean }
  >({
    query: ({ id, ...body }) => ({ url: `/pos/connections/${id}/items/getAll`, method: "POST", body }),
    providesTags: ["posCatalog"],
  }),
  getPosSyncRuns: builder.query<IApiResponse, number>({
    query: (id) => ({ url: `/pos/connections/${id}/sync-runs/getAll`, method: "POST", body: {} }),
    providesTags: ["posSyncRun"],
  }),
  // ---- Menu mapping (Phase 3; appearance only, never publishes) ----
  getPosMappingContext: builder.query<IApiResponse, { id: number; template_id?: number }>({
    query: ({ id, ...body }) => ({ url: `/pos/connections/${id}/mapping-context`, method: "POST", body }),
    providesTags: ["posMapping"],
  }),
  mapPosCategory: builder.mutation<
    IApiResponse,
    { id: number; template_id: number; zone_id: number; pos_category_id: number; playlist_id?: number }
  >({
    query: ({ id, ...body }) => ({ url: `/pos/connections/${id}/mappings`, method: "POST", body }),
    invalidatesTags: ["posMapping", "posDraft"],
  }),
  updatePosMapping: builder.mutation<IApiResponse, { mappingId: number; body: Object }>({
    query: ({ mappingId, body }) => ({ url: `/pos/mappings/${mappingId}`, method: "PUT", body }),
    invalidatesTags: ["posMapping", "posDraft"],
  }),
  reorderPosCategories: builder.mutation<
    IApiResponse,
    { id: number; template_id: number; zone_key: string; mapping_ids: number[] }
  >({
    query: ({ id, ...body }) => ({ url: `/pos/connections/${id}/mappings/reorder-categories`, method: "POST", body }),
    invalidatesTags: ["posMapping", "posDraft"],
  }),
  upsertPosItemOverride: builder.mutation<IApiResponse, { mappingId: number; body: Object }>({
    query: ({ mappingId, body }) => ({ url: `/pos/mappings/${mappingId}/items`, method: "POST", body }),
    invalidatesTags: ["posMapping", "posDraft"],
  }),
  reorderPosItems: builder.mutation<IApiResponse, { mappingId: number; item_ids: number[] }>({
    query: ({ mappingId, item_ids }) => ({ url: `/pos/mappings/${mappingId}/reorder-items`, method: "POST", body: { item_ids } }),
    invalidatesTags: ["posMapping", "posDraft"],
  }),
  deletePosMapping: builder.mutation<IApiResponse, number>({
    query: (mappingId) => ({ url: `/pos/mappings/${mappingId}`, method: "DELETE" }),
    invalidatesTags: ["posMapping", "posDraft"],
  }),
  buildPosDraft: builder.query<IApiResponse, { id: number; template_id: number }>({
    query: ({ id, ...body }) => ({ url: `/pos/connections/${id}/draft`, method: "POST", body }),
    providesTags: ["posDraft"],
  }),
}),


});

export const {
  useGetUserQuery,
  useLoginMutation,
  useCreateCategoryMutation,
  useCreateDuplicateCategoryMutation,
  useCreateModifierCategoryMutation,
  useCreateCompanyMutation,
  useCreateDepartmentMutation,
  useCreateCoursingMutation,
  useCreateTaxMutation,
  useGetDepartmentQuery,
  useCreateProductBrandMutation,
  useCreateProductTagMutation,
  useCreateProductCategoryMutation,
  useCreateProductMutation,
  useCreateEventMutation,
  useCreateVariantMutation,
  useCreateModifierMutation,
  useCreateModifierGroupMutation,
  useCreateModifierGrpMutation,
  useCreatePrinterMutation,
  useGetCategoryQuery,
  useGetModifierCategoryQuery,
  useGetSalesCategoryQuery,
  useGetAllMainCategoryQuery,
  useGetAllMainModifierCategoryQuery,
  useDeleteCoursingMutation,
  useDeleteDepartmentMutation,
  useDeletePrinterMutation,
  useDeleteEmailTemplateMutation,
  useCreateEmailInvoiceMutation,
  useDeleteNoteTemplateMutation,
  useDeleteProductBrandMutation,
  useDeleteProductTagMutation,
  useDeleteTaxMutation,
  useDeleteProductCategoryMutation,
  useDeleteCategoryMutation,
  useReorderCategoryMutation,
  useReorderProductMutation,
  useDeleteModifierCategoryMutation,
  useGetProductQuery,
  useGetEventQuery,
  useGetVariantQuery,
  useGetModifierQuery,
  useGetModifierGroupQuery,
  useGetModifierGrpQuery,
  useCreateGetAllProductMutation,
  useDeleteProductMutation,
  useDeleteEventMutation,
  useDeleteVariantMutation,
  useGetLastProductIdQuery,
  useGetNextProductBarcodeQuery,
  useCreateGetAllModifierMutation,
  useDeleteModifierMutation,
  useDeleteModifierGroupMutation,
  useDeleteModifierGrpMutation,
  useGetTaxQuery,
  useGetCoursingQuery,
  useGetPrinterQuery,
  useGetProductTagQuery,
  useGetProductBrandQuery,
  useGetProductCategoryQuery,
  useGetSubCategoryByCategoryIdQuery,
  useGetSubCategoryByArrayQuery,
  useGetProductSubCategoryQuery,
  useGetAllCategoryNameQuery,
  useGetAllModifierCategoryNameQuery,
  useGetCtegoryFilterQuery,
  useGetLoopingTableQuery,
  useCreateLoopingTableMutation,
  useGetAllLoopingTableQuery,
  useDeleteLoopingTableMutation,
  useUpdateLoopingTableMutation,
  useInsertOrUpdateDataLoopingTableMutation,
  useGetCustomerQuery,
  useGetShopQuery,
  useGetRouteQuery,
  useGetShopProductQuery,
  useGetVendorQuery,
  useGetSerializedItemQuery,
  useGetInventoryQuery,
  useGetSoldQuery,
  useGetBillQuery,
  useDeleteCustomerMutation,
  useDeleteVendorMutation,
  useDeleteShopMutation,
  useDeleteRouteMutation,
  useCreateCustomerMutation,
  useCreateShopMutation,
  useCreateRouteMutation,
  useCreateShopProductMutation,
  useUpdateShopProductPriceMutation,
  useCreateVendorMutation,
  useGetCustomerTransactionQuery,
  useDeleteCustomerTransactionMutation,
  useCreateCustomerTransactionMutation,
  useDeleteCompanyMutation,
  useGetCompanyQuery,
  useGetAllSearchCompanyQuery,
  useCreateWorkOrderMutation,
  useCreateBillMutation,
  useEditInvoiceMutation,
  useGetLastWorkOrderIdQuery,
  useGetLastBillIdQuery,
  useGetWorkOrderQuery,
  useUpdateInvoiceStatusQuery,
  useGetAllInvoiceQuery,
  useGetAllInquiryQuery,
  useGetAllBillQuery,
  useGetAllWorkOrderHistoryQuery,
  useGetAllBillHistoryQuery,
  useGetAllInvoiceHistoryQuery,
  useGetAllAssignDriverHistoryQuery,
  useGetAllWorkOrderHistoryCustomerQuery,
  useGetAllBillHistoryCustomerQuery,
  useCreateDriverMutation,
  useGetDriverQuery,
  useDeleteDriverMutation,
  useGetPriceHistoryQuery,
  useGetBillPriceHistoryQuery,
  useCreateCustomerProjectMutation,
  useCreateCustomerInquiryMutation,
  useDeleteCustomerProjectMutation,
  useDeleteCustomerInquiryMutation,
  useGetCustomerProjectQuery,
  useGetWorkOrderByCustomerProjectQuery,
  useUpdateWorkOrderByCustomerProjectMutation,
  useGetSelectorWorkOrderByCustomerProjectQuery,
  useRemovedCustomerProjectMMutation,
  useCreateWorkOrderWithProjectMutation,
  useCreateEmailMutation,
  useCreateCallMutation,
  useCreateTextMutation,
  useCreateNoteMutation,
  useCreateNoteEmailMutation,
  useCreateEmailTemplateMutation,
  useCreateNoteTemplateMutation,
  useGetEmailTemplateQuery,
  useGetNoteTemplateQuery,
  useGetActivityHistoryQuery,
  useGetActivityHistoryVendorQuery,
  useGetProjectActivityHistoryQuery,
  useGetInquiryActivityHistoryQuery,
  useGetShopOrdersQuery,
  useCreateShopOrderMutation,
  useUpdateShopOrderStatusMutation,
  useGetShopInvoicesQuery,
  useCreateShopInvoiceMutation,
  useGetShopInvoiceQuery,
  useAddShopInvoicePaymentMutation,
  useDeleteShopInvoicePaymentMutation,
  useGetProductSuppliersQuery,
  useLazyGetProductSuppliersQuery,
  useLazyGetSupplierProductsQuery,
  useLinkProductToSupplierMutation,
  useLazyGetProductHistoryQuery,
  useGetProductBarcodesQuery,
  useAddProductBarcodeMutation,
  useSetBarcodePrimaryMutation,
  useDeleteBarcodeMutation,
  useLazyScanBarcodeQuery,
  useGetWarehouseAlertsQuery,
  useReceiveStockMutation,
  useGetWarehouseForecastDataMutation,
  useGetUnitsQuery,
  useCreateUnitMutation,
  useGetTaxGroupsQuery,
  // ─── Digital Signage ────────────────────────────────────────────
  useGetDevicesQuery,
  useGetDeviceQuery,
  useCreateDeviceMutation,
  useResendDeviceActivationMutation,
  useChangeDeviceActivationEmailMutation,
  useViewDeviceActivationCodeMutation,
  useRevokeDeviceMutation,
  useRotateDeviceCredentialsMutation,
  useClaimDevicePairingMutation,
  useUpdateDeviceMutation,
  useDeleteDeviceMutation,
  useAssignDevicePlaylistMutation,
  useGetPlaylistsQuery,
  useGetPlaylistQuery,
  useCreatePlaylistMutation,
  useUpdatePlaylistMutation,
  useDeletePlaylistMutation,
  useAddPlaylistItemMutation,
  useUpdatePlaylistItemMutation,
  useApplyPlaylistDefaultTransitionMutation,
  useRemovePlaylistItemMutation,
  useReorderPlaylistItemsMutation,
  // ─── Templates (V2) ───────────────────────────────────────────────
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useReplaceTemplateZonesMutation,
  useGetMediaQuery,
  useDeleteMediaMutation,
  useBulkDeleteMediaMutation,
  useGenerateAiImageMutation,
  useImproveImagePromptMutation,
  usePreviewImagePromptMutation,
  useGenerateAiTemplateMutation,
  useGetAiGenerationsQuery,
  useGetCompanyLogoQuery,
  useUploadCompanyLogoMutation,
  useGetTemplateZonesQuery,
  useGetDashboardSummaryQuery,
  // ─── Playlist Groups ──────────────────────────────────────────────
  useGetPlaylistGroupsQuery,
  useGetPlaylistGroupQuery,
  useCreatePlaylistGroupMutation,
  useUpdatePlaylistGroupMutation,
  useDeletePlaylistGroupMutation,
  useAddPlaylistToGroupMutation,
  useApplyGroupDefaultTransitionMutation,
  useUpdatePlaylistGroupItemMutation,
  useRemovePlaylistFromGroupMutation,
  useReorderPlaylistGroupItemsMutation,
  useAssignDevicePlaylistGroupMutation,
  useFinalizeOnboardingMutation,
  // ─── Tenant Users + Activity ─────────────────────────────────────
  useGetSignageUsersQuery,
  useCreateSignageUserMutation,
  useUpdateSignageUserMutation,
  useDeleteSignageUserMutation,
  useGetSignageActivityQuery,
  // ─── Smart Menu Scheduling ───────────────────────────────────────
  useCheckScheduleConflictsMutation,
  useGetMenuSchedulesQuery,
  usePreviewScheduleQuery,
  useLazyPreviewScheduleQuery,
  useGetHolidaySuggestionsQuery,
  usePublishPlaylistMutation,
  useGetPlaylistVersionsQuery,
  useRollbackPlaylistMutation,
  useCreateEmergencyOverrideMutation,
  useDeactivateEmergencyOverrideMutation,
  useGetEmergencyOverridesQuery,
  useImproveMenuCopyMutation,
  // ─── POS / Clover menu sync ──────────────────────────────────────
  useGetPosConnectionsQuery,
  useGetPosConnectionQuery,
  useStartPosOauthMutation,
  useExchangePosOauthMutation,
  useDisconnectPosMutation,
  useUpdatePosSettingsMutation,
  useImportPosMenuMutation,
  useSyncPosNowMutation,
  useReconcilePosMutation,
  useGetPosCategoriesQuery,
  useGetPosItemsQuery,
  useGetPosSyncRunsQuery,
  useGetPosMappingContextQuery,
  useMapPosCategoryMutation,
  useUpdatePosMappingMutation,
  useReorderPosCategoriesMutation,
  useUpsertPosItemOverrideMutation,
  useReorderPosItemsMutation,
  useDeletePosMappingMutation,
  useBuildPosDraftQuery,
  // ─── Device Monitoring (admin) ───────────────────────────────────
  useDisableDeviceMutation,
  useEnableDeviceMutation,
  useForceRefreshDeviceMutation,
  useQueueDeviceCommandMutation,
  useGetDeviceCommandsQuery,
  useGetDeviceHeartbeatsQuery,
  useGetDeviceScreenshotQuery,
  // ─── Billing / SaaS ───────────────────────────────────────────────
  useGetBillingReportQuery,
  useUpdateCompanyPlanMutation,
  useExtendTrialMutation,
  useSuspendCompanyMutation,
  useReactivateCompanyMutation,
  useUpdateDeviceLicenseMutation,
  // ─── App Releases ─────────────────────────────────────────────────
  useGetAppReleasesQuery,
  useGetAppReleaseQuery,
  useGetDeployTargetsQuery,
  useUploadAppReleaseMutation,
  useInspectAppReleaseMutation,
  useUpdateAppReleaseMutation,
  useDeleteAppReleaseMutation,
  useDeployAppReleaseMutation,
  usePauseAppReleaseMutation,
  useResumeAppReleaseMutation,
  useRetryFailedAppReleaseMutation,
  useDeactivateAppReleaseMutation,
  // ─── Per-device app-update lifecycle ──────────────────────────────
  useGetDeviceAvailableReleasesQuery,
  useGetDeviceUpdateHistoryQuery,
  useAssignDeviceReleaseMutation,
  useInstallDeviceUpdateMutation,
  useApproveDeviceUpdateMutation,
  useScheduleDeviceUpdateMutation,
  usePauseDeviceUpdateMutation,
  useRetryDeviceUpdateMutation,
  useRollbackDeviceMutation,
  // ─── System diagnostics ────────────────────────────────────────────
  useGetSystemHealthQuery,
  useGetSystemChecklistQuery,
  useGetSystemLogsQuery,
  useGetPilotChecklistQuery,
  useSetPilotChecklistItemMutation,
  useGenerateInstallerPinMutation,
  useRevokeInstallerPinMutation,
  useGetInstallerByPinQuery,
  useGetLatestAndroidReleaseQuery,
  useGetDeviceEventsQuery,
  useGetPilotStatusQuery,
} = attoDeskApi;
